import { websiteEnv } from '@/constants/website-env';
import { cn } from '@/lib/utils';
import clsx from 'clsx';
import Link from 'next/link';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { RemoveScroll } from 'react-remove-scroll';
import { Popover, PopoverArrow, PopoverContent, PopoverTrigger } from '../ui/popover';
import { generateLinkHref } from './generate-link-href';
import { generateNextState } from './generate-next-filter-state';

type Aggregation = NonNullable<NonNullable<NonNullable<GetAllCategoryQuery['allCategories']>['aggregations']>[0]>;

const FixedColorsFamily = ({
  colorsFamilyOptions,
  aggregations,
  optionIdMapToRewrite,
  categoryUrlKey,
  filterState,
  colorOptions,
  hideColorsFamily,
}: {
  colorsFamilyOptions: NonNullable<
    NonNullable<NonNullable<NonNullable<GetCategoryFilterOptionsQuery['products']>['aggregations']>[0]>['options']
  >;
  aggregations: NonNullable<GetAllCategoryQuery['allCategories']>['aggregations'];
  optionIdMapToRewrite: Record<string, Record<string, string>>;
  categoryUrlKey: string;
  filterState: FilterState;
  colorOptions: NonNullable<
    NonNullable<NonNullable<NonNullable<GetCategoryFilterOptionsQuery['products']>['aggregations']>[0]>['options']
  >;
  hideColorsFamily: boolean;
}) => {
  const colorValueMapToAggregation = useMemo<Record<string, Aggregation>>(
    () =>
      aggregations
        ? aggregations?.reduce((acc, cur) => {
            if (!cur || !cur.code || !cur.option_id) return acc;

            if (cur.code !== 'color') {
              return acc;
            }

            acc[cur.option_id] = cur;

            return acc;
          }, {})
        : {},
    [aggregations],
  );
  const prevStateIndex = filterState.findIndex(x => x.attributeCode === 'custom_option_color_ids');
  const selectedColorOptions = prevStateIndex === -1 ? [] : filterState[prevStateIndex].options || [];
  const urlColorRewrites = filterState[prevStateIndex]?.options?.map(x => x.rewrite) || [];
  const [colorsFamilyPopoverVisible, setColorsFamilyPopoverVisible] = useState(false);
  const [disableBodyScroll, setDisableBodyScroll] = useState(false);

  const generateSelectAllNextFilterState = (
    prevIsSelectAll: boolean,
    subColors: {
      label: string;
      rewrite: string;
      value: string;
    }[],
  ) => {
    const prevUrlRewriteMap = urlColorRewrites.reduce<Record<string, 1>>((acc, cur) => {
      acc[cur] = 1;
      return acc;
    }, {});
    const nextSubColorRewriteMap = subColors.reduce<Record<string, 1>>((acc, cur) => {
      if (cur.rewrite) {
        acc[cur.rewrite] = 1;
      }

      return acc;
    }, {});

    if (prevStateIndex === -1) {
      return filterState.concat({
        attributeCode: 'custom_option_color_ids',
        options: subColors,
      });
    }

    const prevFilterOptions = filterState[prevStateIndex]?.options || [];
    let nextFilterOptions = prevFilterOptions;

    if (prevIsSelectAll) {
      nextFilterOptions = prevFilterOptions.filter(x => {
        return nextSubColorRewriteMap[x.rewrite] !== 1;
      });
    } else {
      nextFilterOptions = prevFilterOptions.concat(subColors.filter(x => prevUrlRewriteMap[x.rewrite] !== 1));
    }

    return nextFilterOptions.length > 0
      ? filterState.map((x, i) => {
          return i === prevStateIndex ? { ...x, options: nextFilterOptions } : x;
        })
      : filterState.filter((_, i) => i !== prevStateIndex);
  };

  const generateSubColorNextFilterState = ({
    label,
    rewrite,
    value,
    prevIsActive,
  }: {
    label: string;
    rewrite: string;
    value: string;
    prevIsActive: boolean;
  }) => {
    if (prevStateIndex === -1) {
      return filterState.concat({
        attributeCode: 'custom_option_color_ids',
        options: [
          {
            label,
            rewrite,
            value,
          },
        ],
      });
    }

    const prevFilterOptions = filterState[prevStateIndex].options || [];
    const nextFilterOptions = prevIsActive
      ? prevFilterOptions.filter(x => x.value !== value)
      : prevFilterOptions.concat({ label, value, rewrite });
    return nextFilterOptions.length > 0
      ? filterState.map((x, i) => {
          return i === prevStateIndex ? { ...x, options: nextFilterOptions } : x;
        })
      : filterState.filter((_, i) => i !== prevStateIndex);
  };

  const generateSubColorLink = (param: { label: string; rewrite: string; value: string; prevIsActive: boolean }) => {
    return generateLinkHref({
      categoryUrlKey,
      filterState: generateSubColorNextFilterState(param),
      isSearchPage: false,
      hideColorsFamily: false,
      colorsFamilyOptions,
      optionIdMapToRewrite,
    });
  };
  const generateSelectAllLink = (
    prevIsSelectAll: boolean,
    subColors: {
      label: string;
      rewrite: string;
      value: string;
    }[],
  ) => {
    return generateLinkHref({
      categoryUrlKey,
      filterState: generateSelectAllNextFilterState(prevIsSelectAll, subColors),
      isSearchPage: false,
      hideColorsFamily: false,
      colorsFamilyOptions,
      optionIdMapToRewrite,
    });
  };

  const toggleHeroDashVisible = nextOpen => {
    const heroDashChatWidget = document.getElementById('heroDash-chat-widget-iframe');

    if (!heroDashChatWidget) return;

    if (nextOpen) {
      heroDashChatWidget.style.display = 'none';
    } else {
      heroDashChatWidget.style.display = 'block';
    }
  };

  return (
    <>
      {createPortal(
        colorsFamilyPopoverVisible ? (
          <div
            onClick={() => {
              setColorsFamilyPopoverVisible(false);
              toggleHeroDashVisible(false);
              setDisableBodyScroll(false);
            }}
            className='fixed left-0 top-0 z-[30] h-screen w-screen bg-black/60 supports-[height:100dvh]_h-[100dvh]'
          ></div>
        ) : null,
        document.body,
      )}
      <RemoveScroll enabled={disableBodyScroll}>
        <Popover
          open={colorsFamilyPopoverVisible}
          onOpenChange={nextOpen => {
            setColorsFamilyPopoverVisible(nextOpen);
            toggleHeroDashVisible(nextOpen);
            setDisableBodyScroll(nextOpen);
          }}
        >
          <PopoverTrigger asChild>
            <button
              type='button'
              className='flex w-full items-center justify-center gap-x-[5px] rounded-[3px] bg-f8f3ef py-2.5 '
            >
              <span className='text-[13px] font-medium leading-[1.4] text-80583F'>
                {websiteEnv.isUnitedKingdom ? 'COLOUR' : 'COLOR'}
              </span>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='10'
                height='15'
                fill='none'
                viewBox='0 0 10 15'
              >
                <path
                  fill='#80583F'
                  d='M10 5H7.93L5 7.93 2.07 5H0l5 5z'
                ></path>
              </svg>
            </button>
          </PopoverTrigger>
          <PopoverContent
            sideOffset={10}
            className='z-[70] flex max-h-[360px] w-screen flex-wrap gap-2.5 overflow-auto bg-white px-2.5 pb-5 pt-2.5'
          >
            {hideColorsFamily
              ? colorOptions.map(option => {
                  if (!option) return null;

                  const isSelected = selectedColorOptions.some(x => option.value === x.value);
                  const colorRectStyle = createSubColorStyle(option.swatch_data?.thumbnail, option.swatch_data?.value);
                  const nextState = generateNextState({
                    attributeCode: 'custom_option_color_ids',
                    value: option.value,
                    label: option.label || '',
                    prevStateIndex: prevStateIndex,
                    prevActive: isSelected,
                    filterState,
                    optionIdMapToRewrite,
                    isSearchPage: false,
                    hideColorsFamily,
                  });
                  const href = generateLinkHref({
                    categoryUrlKey,
                    filterState: nextState,
                    isSearchPage: false,
                    hideColorsFamily,
                    colorsFamilyOptions,
                    optionIdMapToRewrite,
                  });

                  return (
                    <Link
                      className='flex w-[60px] flex-none flex-col items-center'
                      key={option.value}
                      href={href}
                      onClick={() => {
                        setColorsFamilyPopoverVisible(false);
                        toggleHeroDashVisible(false);
                        setDisableBodyScroll(false);
                      }}
                    >
                      <div
                        className={cn(
                          ' block h-10 w-10 overflow-hidden rounded-[50%] border-2 border-solid  p-[3px]',
                          isSelected ? 'border-80583F' : 'border-transparent',
                        )}
                        aria-label={option.label || undefined}
                      >
                        <div
                          style={colorRectStyle}
                          className={clsx(
                            'h-full rounded-[50%] bg-cover',
                            isWhite(option.swatch_data?.value || '') ? ' border-1 border-solid border-E7DED5' : '',
                          )}
                        ></div>
                      </div>
                      <div className={clsx('text-center text-[13px] leading-[1.4]', 'cursor-pointer')}>
                        {option.label}
                      </div>
                    </Link>
                  );
                })
              : colorsFamilyOptions?.map((colorsFamily, index) => {
                  if (!colorsFamily) {
                    return null;
                  }

                  const values = colorsFamily.value?.split(',');
                  const subColorOptions = values?.reduce<Aggregation[]>((acc, cur) => {
                    const aggregationValue = colorValueMapToAggregation[cur];

                    if (
                      aggregationValue &&
                      aggregationValue.label &&
                      aggregationValue.option_id &&
                      aggregationValue.rewrite
                    ) {
                      acc.push(aggregationValue);
                    }

                    return acc;
                  }, []);

                  if (!Array.isArray(subColorOptions) || subColorOptions.length === 0) {
                    return null;
                  }

                  return (
                    <ColorsFamilyItem
                      key={index}
                      swatchData={colorsFamily.swatch_data || null}
                      colorsFamilyLabel={colorsFamily.label || ''}
                      subColorOptions={subColorOptions}
                      urlColorRewrites={urlColorRewrites}
                      generateSubColorLink={generateSubColorLink}
                      generateSelectAllLink={generateSelectAllLink}
                      onGoNextPage={() => {
                        setColorsFamilyPopoverVisible(false);
                        toggleHeroDashVisible(true);
                        setDisableBodyScroll(false);
                      }}
                    />
                  );
                })}
          </PopoverContent>
        </Popover>
      </RemoveScroll>
    </>
  );
};

const ColorsFamilyItem = ({
  subColorOptions,
  generateSubColorLink,
  generateSelectAllLink,
  urlColorRewrites,
  swatchData,
  colorsFamilyLabel,
  onGoNextPage,
}: {
  subColorOptions: Aggregation[];
  urlColorRewrites: string[];
  generateSubColorLink: (param: { label: string; rewrite: string; value: string; prevIsActive: boolean }) => string;
  generateSelectAllLink: (
    prevIsSelectAll: boolean,
    subColors: {
      label: string;
      rewrite: string;
      value: string;
    }[],
  ) => string;
  colorsFamilyLabel: string;
  swatchData: Aggregation['swatch_data'];
  onGoNextPage: () => void;
}) => {
  const curColorsFamilySubColorRewrites: string[] = subColorOptions.reduce<string[]>((acc, cur) => {
    if (cur.rewrite) {
      acc.push(cur.rewrite);
    }

    return acc;
  }, []);
  const curColorsFamilySubColorRewriteMap = curColorsFamilySubColorRewrites.reduce<Record<string, true>>((acc, cur) => {
    acc[cur] = true;

    return acc;
  }, {});
  const colorsFamilyRewrite = colorsFamilyLabel.toLowerCase().replace(/\s/g, '-');
  const [subColorPopoverVisible, setSubColorPopoverVisible] = useState(false);
  const { curColorFamilyHasSelected, curColorFamilySelectedCount, allSelectedColorRewriteMap } = (() => {
    const allSelectedColorRewriteMap: Record<string, true> = {};
    let curColorFamilyHasSelected = false;
    let curColorFamilySelectedCount = 0;

    for (const urlColorRewrite of urlColorRewrites) {
      allSelectedColorRewriteMap[urlColorRewrite] = true;

      if (curColorsFamilySubColorRewriteMap[urlColorRewrite]) {
        curColorFamilyHasSelected = true;
        curColorFamilySelectedCount++;
      }
    }

    return {
      curColorFamilyHasSelected,
      curColorFamilySelectedCount: Math.min(curColorFamilySelectedCount, subColorOptions.length),
      allSelectedColorRewriteMap,
    };
  })();

  const isSelectedAll =
    curColorFamilyHasSelected && curColorFamilySelectedCount === curColorsFamilySubColorRewrites.length;
  const colorRectStyle = createColorStyle(swatchData?.thumbnail, swatchData?.value);

  const nextSelectAllLink = generateSelectAllLink(
    isSelectedAll,
    subColorOptions.reduce<{ label: string; value: string; rewrite: string }[]>((acc, x) => {
      if (x && x.rewrite && x.option_id && x.label) {
        acc.push({
          label: x.label,
          value: x.option_id,
          rewrite: x.rewrite,
        });
      }

      return acc;
    }, []),
  );
  const isColorAll = colorsFamilyRewrite === 'all';

  return (
    <Popover
      open={subColorPopoverVisible}
      onOpenChange={setSubColorPopoverVisible}
    >
      <PopoverTrigger asChild>
        <div className='flex w-[60px] flex-none flex-col items-center'>
          <div
            className={cn(
              ' block h-10 w-10 overflow-hidden rounded-[50%] border-2 border-solid p-[3px]',
              curColorFamilyHasSelected ? 'border-80583F' : 'border-transparent',
            )}
            aria-label={colorsFamilyLabel}
          >
            <div
              style={isColorAll ? undefined : colorRectStyle}
              className={clsx(
                isWhite(swatchData?.value || '') ? 'border-1 border-solid border-E7DED5' : '',
                'h-full rounded-[50%] bg-cover',
              )}
            >
              {isColorAll ? (
                <div className='h-full overflow-hidden rounded-[50%]'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='67'
                    height='66'
                    fill='none'
                    viewBox='0 0 67 66'
                    className='h-auto w-full'
                  >
                    <g clipPath='url(#clip0_13530_35876)'>
                      <path
                        fill='#EFEFE6'
                        d='M.5 0h22v22H.5z'
                      />
                      <path
                        fill='#E0D1BD'
                        d='M22.5 0h22v22h-22z'
                      />
                      <path
                        fill='#D68B59'
                        d='M44.5 0h22v22h-22z'
                      />
                      <path
                        fill='#F0D3C9'
                        d='M.5 22h22v22H.5z'
                      />
                      <path
                        fill='#CC8986'
                        d='M22.5 22h22v22h-22z'
                      />
                      <path
                        fill='#9A1834'
                        d='M44.5 22h22v22h-22z'
                      />
                      <path
                        fill='#C4C3D0'
                        d='M.5 44h22v22H.5z'
                      />
                      <path
                        fill='#6F6E71'
                        d='M22.5 44h22v22h-22z'
                      />
                      <path
                        fill='#002712'
                        d='M44.5 44h22v22h-22z'
                      />
                    </g>
                    <defs>
                      <clipPath id='clip0_13530_35876'>
                        <rect
                          width='66'
                          height='66'
                          x='0.5'
                          fill='#fff'
                          rx='5'
                        />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
              ) : null}
            </div>
          </div>

          <div className={clsx('text-[13px] leading-[1.4]', 'cursor-pointer')}>
            {colorsFamilyLabel}
            {curColorFamilySelectedCount > 0 ? `(${curColorFamilySelectedCount})` : null}
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className='z-[80] rounded-[10px] border-1 border-solid border-E7DED5 bg-white'
        side='bottom'
        sideOffset={0}
        collisionPadding={{
          left: 10,
          right: 10,
        }}
      >
        <div
          className={clsx(
            'max-h-[294px]',
            'list-none gap-2.5 overflow-y-auto overscroll-none ',
            'px-1.5 py-[25px]',
            'category-color-select',
          )}
        >
          {!isColorAll && (
            <div className='mb-[15px] pl-2.5'>
              <Link
                href={nextSelectAllLink}
                onClick={() => {
                  setSubColorPopoverVisible(false);
                  onGoNextPage();
                }}
                className='flex items-center gap-3'
              >
                <span
                  className={clsx(
                    'h-5 w-5',
                    'border-1 border-solid border-80583F',
                    isSelectedAll ? 'bg-80583F' : 'bg-transparent',
                    'flex items-center justify-center',
                  )}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='15'
                    height='13'
                    fill='none'
                    viewBox='0 0 15 13'
                    className={clsx(!isSelectedAll && 'hidden')}
                  >
                    <path
                      stroke='#fff'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='1.5'
                      d='m13.5 1.305-8.4 10-3.6-3.75'
                    />
                  </svg>
                </span>
                <span className='text-sm leading-5 text-#403732'>SELECT ALL</span>
              </Link>
            </div>
          )}

          <div
            className={clsx(
              'flex flex-wrap',
              subColorOptions.length > 5 && 'lg_grid lg_grid-cols-5',
              ' max-w-[calc(100vw_-_34px)]',
              'gap-y-2.5',
              isColorAll ? 'gap-x-[9px]' : 'gap-x-2.5',
            )}
          >
            {subColorOptions.map((colorOption: any, index: number) => {
              if (!colorOption) {
                return null;
              }

              const colorRewrite = colorOption.rewrite;
              const swatch_data = colorOption.swatch_data;
              const isSelected = isSelectedAll || allSelectedColorRewriteMap[colorRewrite];
              const nextSubColorLink = generateSubColorLink({
                prevIsActive: isSelected,
                label: colorOption.label,
                value: colorOption.option_id,
                rewrite: colorRewrite,
              });
              const colorRectStyle = createSubColorStyle(swatch_data?.thumbnail, swatch_data?.value);

              return (
                <Link
                  href={nextSubColorLink}
                  key={index}
                  onClick={() => {
                    setSubColorPopoverVisible(false);
                    onGoNextPage();
                  }}
                  className='flex cursor-pointer flex-col items-center justify-start'
                >
                  <div
                    className={cn(
                      ' h-10 w-10 rounded-[50%] border-2 border-solid bg-clip-content p-[3px]',
                      isSelected ? 'border-80583F' : 'border-transparent',
                    )}
                  >
                    <div
                      style={colorRectStyle}
                      className={cn(
                        'h-full rounded-[50%] bg-cover ',
                        isWhite(swatch_data?.value || '') ? 'border-1 border-E7DED5' : '',
                      )}
                    />
                  </div>

                  <span className={clsx(' text-center text-[13px] leading-[1.4] text-#403732', 'w-[60px]')}>
                    {websiteEnv.isUnitedKingdom && colorOption?.label === 'Multi Color'
                      ? 'Multicolour'
                      : colorOption?.label}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
        <PopoverArrow asChild>
          <span className='block h-2.5 w-4 -translate-y-[2px] bg-white'>
            <span
              className={clsx(
                ' relative block h-[9px] w-4 translate-y-[5px]  bg-white',
                'before_absolute before_h-3 before_w-[1px] before_bg-E7DED5 before_content-[""]',
                'before_left-1/2 before_top-1/2 before_origin-top before_-translate-x-1/2 before_rotate-[135deg] ',
                'after_absolute after_h-3 after_w-[1px] after_bg-E7DED5 after_content-[""]',
                'after_left-1/2 after_top-1/2 after_origin-top after_-translate-x-1/2 after_-rotate-[135deg] ',
              )}
            ></span>
          </span>
        </PopoverArrow>
      </PopoverContent>
    </Popover>
  );
};

const isWhite = (value: string) => {
  const lowercaseValue = value.toLowerCase();

  return lowercaseValue === '#fff' || lowercaseValue === '#ffffff';
};

const createColorStyle = (thumbnail: string | undefined | null, value: string | undefined | null) => {
  if (thumbnail && thumbnail.indexOf('#') < 0) {
    return {
      backgroundImage: `url("${thumbnail}")`,
    };
  }

  return {
    backgroundColor: value || undefined,
  };
};

const createSubColorStyle = (thumbnail: string | undefined | null, value: string | undefined | null) => {
  if (thumbnail && thumbnail.indexOf('#') < 0) {
    return {
      backgroundImage: `url("${process.env.NEXT_PUBLIC_BASE_PATH}/media/attribute/swatch/swatch_thumb/110x90${thumbnail}")`,
    };
  }

  return {
    backgroundColor: value || undefined,
  };
};

export default FixedColorsFamily;
